import { toast } from 'react-toastify';
import { useUserStore } from '@Auth/store';
import { ApiPatchOperation, AuthErrorCode } from '@root/@types/types';
import type { AxiosError, AxiosResponse } from 'axios';
import { get, isEmpty } from 'lodash-es';

import { VERSION_MISMATCH_TOAST_ID, VersionMismatchToast } from './VersionMismatchToast';

const OMIT_ERROR_TOASTS = [
  {
    testUrl: (url: string) => url.split('/').includes('templates'),
    method: 'get',
    status: 404,
  },
  {
    testUrl: (url: string) => url.split('/').includes('submissions'),
    method: 'get',
    status: 404,
  },
  {
    testUrl: (url: string) => url.split('/').includes('branding'),
    method: 'get',
    status: 404,
  },
];

const isSyncDataError = (error: AxiosError) => {
  const urlMatches = error.config?.url?.split('/').includes('submissions') ?? false;

  let parsedRequestData: Record<string, any> = {};

  try {
    const requestData = get(error, 'config.data', '{}');
    parsedRequestData = JSON.parse(requestData);
  } catch {
    // Do nothing
  }

  return (
    urlMatches &&
    error.config?.method === 'patch' &&
    error.response?.status === 400 &&
    parsedRequestData.operation === ApiPatchOperation.SyncSubmissionData
  );
};

const shouldSkipToast = (error: any): boolean => {
  if (isSyncDataError(error)) {
    return true;
  }

  return OMIT_ERROR_TOASTS.some((i) => {
    const urlMatches = i.testUrl(error.config.url);
    return urlMatches && i.method === error.config.method && i.status === error.response.status;
  });
};

/**
 * Check if the user is opening a new tab by checking if there is an aliasId in the url and no user in session storage
 */
const isNewTab = () => {
  const urlParams = new URLSearchParams(window.location.search);
  const aliasId = urlParams.get('User');
  const userInStorage = useUserStore.getState().user;

  return aliasId && !userInStorage;
};

const isNewTabWithWrongAlias = (artificialErrorCode?: number) => {
  return artificialErrorCode === AuthErrorCode.AliasIdMismatch && isNewTab();
};

const isNewTabWithNoSession = (artificialErrorCode?: number) =>
  isNewTab() && (!artificialErrorCode || artificialErrorCode === AuthErrorCode.SessionNotFound);

const isNewTabWithExpiredSession = (artificialErrorCode?: number) =>
  isNewTab() &&
  (artificialErrorCode === AuthErrorCode.SessionExpiredWithIdleTimeout ||
    artificialErrorCode === AuthErrorCode.SessionExpiredWithAbsoluteTimeout);

const isNoSessionIdInCookie = (artificialErrorCode?: number) =>
  artificialErrorCode === AuthErrorCode.NoSessionIdInCookie;

const isEntra = import.meta.env?.VITE_AUTH_METHOD === 'entra';

export const AxiosErrorInterceptor = (error: AxiosError): Promise<AxiosError> => {
  const responseStatusCode = error.response?.status ?? 200;

  if ([401, 403].includes(responseStatusCode)) {
    const artificialErrorCode = parseInt(get(error, 'response.data.artificialErrorCode') ?? '') || 0;
    /*
    | In these scenarios, we want to skip showing the auth error page
    */
    if (!isEntra) return Promise.reject(error);
    if (isNewTabWithWrongAlias(artificialErrorCode)) return Promise.reject(error);
    if (isNewTabWithNoSession(artificialErrorCode)) return Promise.reject(error);
    if (isNewTabWithExpiredSession(artificialErrorCode)) return Promise.reject(error);
    if (isNoSessionIdInCookie(artificialErrorCode)) return Promise.reject(error);

    useUserStore.getState().clearUser();
    useUserStore.getState().setAuthErrorCode(artificialErrorCode || AuthErrorCode.FailedAuthWithoutServerCode);

    return Promise.reject(error);
  }

  if (responseStatusCode >= 400 && responseStatusCode <= 599) {
    if (!shouldSkipToast(error)) {
      const formattedMessage =
        get(error, 'response.data.message') || get(error, 'response.data.error.message') || 'Server Error';

      toast.error(formattedMessage, {
        // 3 min
        autoClose: 3 * 60 * 1000,
        pauseOnHover: true,
      });
    }
  }

  return Promise.reject(error);
};

export const AxiosResponseInterceptor = (response: AxiosResponse): AxiosResponse => {
  const serverVersion = response.headers['x-app-version'];
  const clientVersion = import.meta.env.VITE_VERSION;

  const hasServerVersion = !isEmpty(serverVersion);
  const hasClientVersion = !isEmpty(clientVersion);

  if (hasServerVersion && hasClientVersion && serverVersion !== clientVersion) {
    toast.info(<VersionMismatchToast running={clientVersion} latest={serverVersion} />, {
      toastId: VERSION_MISMATCH_TOAST_ID,
      containerId: VERSION_MISMATCH_TOAST_ID,
      autoClose: false,
      closeOnClick: false,
      draggable: false,
      className: 'max-w-[384px] items-start!',
      closeButton: false,
    });
  }

  return response;
};
